import React from 'react'
import { Skeleton } from '../../ui/skeleton'
import { getInsightsRouteState } from '../../../routing/insightsRouteState'
import AuthenticatedAppFallback from '../../Auth/AuthenticatedAppFallback'
import { PropertySidebarLoadingContent } from './PropertySidebarLoadingContent'

const DETAIL_TITLES = {
  collections: 'Delinquency',
  escalations: 'Escalation Insights',
  leasing: 'Leasing Insights',
  'move-in-surveys': 'Move-In Surveys',
}

const getRouteTitle = (pathname, basePath = '/insights') => {
  const route = pathname.slice(basePath.length).replace(/^\//, '')

  if (!route) {
    return 'Insights'
  }

  return DETAIL_TITLES[route] || 'Insights'
}

const CollapsedPropertySidebarFallback = () => (
  <div className="flex w-[56px] shrink-0 flex-col items-center border-r border-border bg-background py-4">
    <Skeleton className="h-9 w-9 rounded-lg" />
    <Skeleton className="mt-4 h-10 w-10 rounded-lg" />
    <Skeleton className="mt-2 h-3 w-4 rounded" />
    <Skeleton className="mt-auto mb-8 h-24 w-3 rounded" />
  </div>
)

const ExpandedPropertySidebarFallback = () => (
  <div className="w-[240px] shrink-0 border-r border-border bg-background">
    <PropertySidebarLoadingContent />
  </div>
)

const DetailContentFallback = ({ pathname, basePath, isChromeCompanion }) => {
  const title = getRouteTitle(pathname, basePath)

  return (
    <div className="flex-1 overflow-y-auto bg-background">
      <div className={`mx-auto flex w-full max-w-[1536px] flex-col pb-8 ${isChromeCompanion ? 'min-h-full px-3' : 'min-h-screen px-8'}`}>
        <div className="mb-6 flex items-center gap-2 border-b border-border py-4 text-sm text-muted-foreground">
          <Skeleton className="h-4 w-4 rounded-full" />
          <span>Insights</span>
          <span>/</span>
          <span className="font-semibold text-foreground">{title}</span>
          <span>·</span>
          <Skeleton className="h-4 w-28 rounded" />
        </div>

        <div className="mb-8">
          <h1 className="text-3xl tracking-tight text-foreground">{title}</h1>
          <Skeleton className="mt-4 h-4 w-40 rounded" />
          <Skeleton className="mt-4 h-4 w-28 rounded" />
        </div>

        <div className="mb-8 flex flex-wrap items-center gap-4 border-b border-border pb-5">
          <div className="flex gap-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-9 w-28 rounded-xl" />
            ))}
          </div>
          <div className="ml-auto flex gap-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-9 w-12 rounded-xl" />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="rounded-2xl border border-border bg-card p-5">
              <Skeleton className="h-4 w-24 rounded" />
              <Skeleton className="mt-4 h-10 w-24 rounded" />
              <Skeleton className="mt-4 h-4 w-32 rounded" />
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-2xl border border-border bg-card p-6">
          <Skeleton className="h-8 w-56 rounded" />
          <Skeleton className="mt-4 h-4 w-80 rounded" />
          <Skeleton className="mt-6 h-[360px] w-full rounded-2xl" />
        </div>
      </div>
    </div>
  )
}

const SummaryContentFallback = ({ isChromeCompanion }) => (
  <div className="flex-1 overflow-y-auto bg-background">
    <div className={`mx-auto flex w-full max-w-[1536px] flex-col pb-8 ${isChromeCompanion ? 'min-h-full px-3' : 'min-h-screen px-8'}`}>
      <div className="px-0 pt-8 pb-6">
        <h1 className="text-3xl tracking-tight text-foreground">Insights</h1>
        <p className="mt-2 text-base text-muted-foreground">
          Portfolio performance metrics across selected properties
        </p>
      </div>

      <div className="pb-6">
        <div className="flex h-16 items-center rounded-xl border border-border px-4">
          <Skeleton className="h-2 w-2 rounded-full" />
          <span className="ml-3 text-sm font-medium text-muted-foreground">Portfolio</span>
          <span className="mx-3 text-sm text-muted-foreground">•</span>
          <Skeleton className="h-4 w-32 rounded" />
        </div>
      </div>
    </div>
  </div>
)

export function InsightsRouteFallback({
  basePath = '/insights',
  isChromeCompanion = false,
  pathname = '/insights',
  search = '',
}) {
  const { isDetailRoute, isPropertySidebarCollapsed } = getInsightsRouteState({
    pathname,
    search,
    basePath,
  })

  return (
    <div className={`flex flex-1 bg-background ${isChromeCompanion ? 'min-h-0' : 'min-h-screen'}`}>
      {!isChromeCompanion && (isPropertySidebarCollapsed
        ? <CollapsedPropertySidebarFallback />
        : <ExpandedPropertySidebarFallback />)}
      {isDetailRoute
        ? (
          <DetailContentFallback
            pathname={pathname}
            basePath={basePath}
            isChromeCompanion={isChromeCompanion}
          />
        )
        : <SummaryContentFallback isChromeCompanion={isChromeCompanion} />}
    </div>
  )
}

export function InsightsBootstrapFallback({ isChromeCompanion = false, ...props }) {
  if (isChromeCompanion) {
    return <InsightsRouteFallback {...props} isChromeCompanion />
  }

  return (
    <AuthenticatedAppFallback>
      <InsightsRouteFallback {...props} />
    </AuthenticatedAppFallback>
  )
}

export default InsightsBootstrapFallback
